import React, { useState } from 'react';

// Components
import WpFeedbackButtons from '../WpFeedbackButtons';

const FeedbackButtonsDocs = () => {
  const [basicResponse, setBasicResponse] = useState<boolean | null>(null);
  const [surveyResponses, setSurveyResponses] = useState<{ [key: number]: boolean | null }>({});
  const [customLabelResponse, setCustomLabelResponse] = useState<boolean | null>(null);
  const [disabledResponse] = useState<boolean | null>(true);
  const [iconOnlyResponse, setIconOnlyResponse] = useState<boolean | null>(null);

  // Size examples state
  const [smResponse, setSmResponse] = useState<boolean | null>(null);
  const [mdResponse, setMdResponse] = useState<boolean | null>(null);
  const [lgResponse, setLgResponse] = useState<boolean | null>(null);

  // Icon-only size examples state
  const [iconSmResponse, setIconSmResponse] = useState<boolean | null>(null);
  const [iconMdResponse, setIconMdResponse] = useState<boolean | null>(null);
  const [iconLgResponse, setIconLgResponse] = useState<boolean | null>(null);

  const sizes = ['sm', 'md', 'lg'] as const;
  const responses = [smResponse, mdResponse, lgResponse];
  const sizeSetters = [setSmResponse, setMdResponse, setLgResponse];

  // Icon-only examples arrays
  const iconResponses = [iconSmResponse, iconMdResponse, iconLgResponse];
  const iconSizeSetters = [setIconSmResponse, setIconMdResponse, setIconLgResponse];

  const surveyQuestions = [
    'Do you find this component useful?',
    'Would you recommend it to others?',
    'Is the documentation clear?',
  ];

  const handleSurveyResponse = (questionIndex: number, response: boolean) => {
    setSurveyResponses(prev => ({ ...prev, [questionIndex]: response }));
  };

  const noop = () => {
    // No-op function for display-only examples
  };

  const getBasicStatusText = () => {
    if (basicResponse === null) return 'None';
    return basicResponse ? 'Yes' : 'No';
  };

  const getCustomLabelStatusText = () => {
    if (customLabelResponse === null) return 'None';
    return customLabelResponse ? 'Agree' : 'Disagree';
  };

  const getIconOnlyStatusText = () => {
    if (iconOnlyResponse === null) return 'None';
    return iconOnlyResponse ? 'Yes' : 'No';
  };

  return (
    <>
      <h5>Basic Usage</h5>
      <div className="mb-4">
        <WpFeedbackButtons onResponse={setBasicResponse} selectedResponse={basicResponse} qaTest="basic-example" />
        <p className="text-muted mt-2">Selected: {getBasicStatusText()}</p>
      </div>

      <hr />

      <h5>Sizes</h5>
      {sizes.map((size, index) => (
        <div className="mb-3" key={size}>
          <div className="d-flex align-items-center">
            <span className="mr-3" style={{ minWidth: '40px' }}>
              {size}:
            </span>
            <WpFeedbackButtons
              onResponse={sizeSetters[index]}
              selectedResponse={responses[index]}
              size={size}
              qaTest={`size-${size}`}
            />
          </div>
        </div>
      ))}

      <hr />

      <h5>Custom Labels</h5>
      <div className="mb-4">
        <WpFeedbackButtons
          onResponse={setCustomLabelResponse}
          selectedResponse={customLabelResponse}
          positiveLabel="Agree"
          negativeLabel="Disagree"
          qaTest="custom-labels"
        />
        <p className="text-muted mt-2">Selected: {getCustomLabelStatusText()}</p>
      </div>

      <hr />

      <h5>Survey Questions</h5>
      <div className="mb-4">
        {surveyQuestions.map((question, index) => (
          <div key={question} className="mb-3 p-3 border rounded">
            <p className="mb-2">{question}</p>
            <WpFeedbackButtons
              onResponse={response => handleSurveyResponse(index, response)}
              selectedResponse={surveyResponses[index]}
              size="sm"
              qaTest={`survey-${index}`}
            />
          </div>
        ))}
      </div>

      <hr />

      <h5>Icon Only</h5>
      <div className="mb-4">
        <WpFeedbackButtons
          onResponse={setIconOnlyResponse}
          selectedResponse={iconOnlyResponse}
          iconOnly={true}
          qaTest="icon-only-example"
        />
        <p className="text-muted mt-2">Selected: {getIconOnlyStatusText()}</p>
      </div>

      <hr />

      <h5>Icon Only - All Sizes</h5>
      {sizes.map((size, index) => (
        <div className="mb-3" key={`icon-${size}`}>
          <div className="d-flex align-items-center">
            <span className="mr-3" style={{ minWidth: '40px' }}>
              {size}:
            </span>
            <WpFeedbackButtons
              onResponse={iconSizeSetters[index]}
              selectedResponse={iconResponses[index]}
              size={size}
              iconOnly={true}
              qaTest={`icon-size-${size}`}
            />
          </div>
        </div>
      ))}

      <hr />

      <h5>Disabled State</h5>
      <div className="mb-4">
        <div className="mb-3">
          <h6>With Text</h6>
          <WpFeedbackButtons
            onResponse={noop}
            selectedResponse={disabledResponse}
            disabled={true}
            qaTest="disabled-example"
          />
          <p className="text-muted mt-2">This component is disabled with a pre-selected response</p>
        </div>
        <div className="mb-3">
          <h6>Icon Only</h6>
          <WpFeedbackButtons
            onResponse={noop}
            selectedResponse={disabledResponse}
            disabled={true}
            iconOnly={true}
            qaTest="disabled-icon-only-example"
          />
          <p className="text-muted mt-2">Icon-only version, disabled with a pre-selected response</p>
        </div>
      </div>

      <hr />

      <h5>All States</h5>
      <div className="mb-4">
        <h6>With Text</h6>
        <div className="row mb-3">
          <div className="col-md-4">
            <h6>No Selection</h6>
            <WpFeedbackButtons onResponse={noop} selectedResponse={null} qaTest="state-none" />
          </div>
          <div className="col-md-4">
            <h6>Positive Selected</h6>
            <WpFeedbackButtons onResponse={noop} selectedResponse={true} qaTest="state-positive" />
          </div>
          <div className="col-md-4">
            <h6>Negative Selected</h6>
            <WpFeedbackButtons onResponse={noop} selectedResponse={false} qaTest="state-negative" />
          </div>
        </div>
        <h6>Icon Only</h6>
        <div className="row">
          <div className="col-md-4">
            <h6>No Selection</h6>
            <WpFeedbackButtons onResponse={noop} selectedResponse={null} iconOnly={true} qaTest="state-icon-none" />
          </div>
          <div className="col-md-4">
            <h6>Positive Selected</h6>
            <WpFeedbackButtons onResponse={noop} selectedResponse={true} iconOnly={true} qaTest="state-icon-positive" />
          </div>
          <div className="col-md-4">
            <h6>Negative Selected</h6>
            <WpFeedbackButtons
              onResponse={noop}
              selectedResponse={false}
              iconOnly={true}
              qaTest="state-icon-negative"
            />
          </div>
        </div>
      </div>

      <hr />

      <h5>Different Label Combinations</h5>
      <div className="mb-4">
        <div className="row">
          <div className="col-md-6 mb-3">
            <h6>Like / Dislike</h6>
            <WpFeedbackButtons
              onResponse={noop}
              selectedResponse={null}
              positiveLabel="Like"
              negativeLabel="Dislike"
              qaTest="like-dislike"
            />
          </div>
          <div className="col-md-6 mb-3">
            <h6>Helpful / Not Helpful</h6>
            <WpFeedbackButtons
              onResponse={noop}
              selectedResponse={null}
              positiveLabel="Helpful"
              negativeLabel="Not Helpful"
              qaTest="helpful"
            />
          </div>
          <div className="col-md-6 mb-3">
            <h6>Accept / Reject</h6>
            <WpFeedbackButtons
              onResponse={noop}
              selectedResponse={null}
              positiveLabel="Accept"
              negativeLabel="Reject"
              qaTest="accept-reject"
            />
          </div>
          <div className="col-md-6 mb-3">
            <h6>Approve / Deny</h6>
            <WpFeedbackButtons
              onResponse={noop}
              selectedResponse={null}
              positiveLabel="Approve"
              negativeLabel="Deny"
              qaTest="approve-deny"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FeedbackButtonsDocs;
